/**
 * smbCredentialStore — OS-keychain-backed (Rust `keyring`) store for
 * PROGRAMMATIC SMB connector credentials, keyed per connector.
 *
 * A programmatic-SMB filesystem connector has no OS mount, so its sync needs the
 * share host + credentials on every run (the wizard's first sync AND every later
 * auto/nightly sync). We persist them in the SAME secret vault the desktop OAuth
 * tokens use (`getSecretStorage`, OS keychain via Rust `keyring` in Tauri) — NEVER in plaintext
 * React state beyond the connect form, and NEVER on the Rust side (the Rust
 * `SmbConfig` is passed per-call and dropped). This mirrors the desktop token
 * store + the `LdapConfig` "creds threaded per-call" contract.
 *
 * The record also carries the share-relative scan `roots` so the sync path has
 * everything it needs without re-deriving them from the connector's `smb://…`
 * `base_paths`.
 */

import { getSecretStorage } from "@ando/core/storage";

import type { SmbConfig } from "./fsBridge";

/** Persisted SMB connection record: credentials + share-relative scan roots. */
export interface SmbCredentialRecord extends SmbConfig {
  /** Share-relative scan roots (`""` ⇒ the whole share). */
  readonly roots: readonly string[];
}

/** Per-connector vault key. Bumped suffix (`v1`) lets the shape evolve. */
function vaultKey(connectorId: string): string {
  return `ando.desktop.smb.${connectorId}.v1`;
}

/** Persist (or overwrite) a connector's SMB credentials. Best-effort like the
 * token store — a vault write failure must not crash the connect flow. */
export async function saveSmbCredentials(
  connectorId: string,
  record: SmbCredentialRecord,
): Promise<void> {
  try {
    await getSecretStorage().set(vaultKey(connectorId), record);
  } catch {
    // best-effort
  }
}

/** Read a connector's SMB credentials, or `null` when absent / unreadable. */
export async function readSmbCredentials(
  connectorId: string,
): Promise<SmbCredentialRecord | null> {
  try {
    return (await getSecretStorage().get<SmbCredentialRecord>(vaultKey(connectorId))) ?? null;
  } catch {
    return null;
  }
}

/** Forget a connector's SMB credentials (on disconnect). Best-effort. */
export async function removeSmbCredentials(connectorId: string): Promise<void> {
  try {
    await getSecretStorage().remove(vaultKey(connectorId));
  } catch {
    // best-effort
  }
}
